import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, UserRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { Field, inp } from "@/components/admin/AdminUi";
import { STATUS, STATUS_KEYS, todayIso } from "./utils";

function Suggestions({ q, onPick }) {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) return setList([]);
    const t = setTimeout(() => api.get("/admin/counter-orders/suggest", { params: { q } }).then((r) => setList(r.data)).catch(() => setList([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  if (!list.length) return null;
  return (
    <div className="absolute z-20 left-0 right-0 mt-1 rounded-md border bg-white shadow-lg overflow-hidden" data-testid="counter-suggestions">
      {list.map((u) => (
        <button type="button" key={u.id} data-testid={`counter-suggest-${u.id}`} onMouseDown={(e) => { e.preventDefault(); onPick(u); setList([]); }}
          className="w-full text-left px-3 py-2 text-sm hover:bg-brand-yellow/20 flex items-center gap-2">
          <UserRound className="w-4 h-4 text-slate-400" />
          <span className="font-semibold">{u.name}</span><span className="text-xs text-slate-500 truncate">{u.email}{u.phone && ` · ${u.phone}`}</span>
        </button>
      ))}
    </div>
  );
}

const EMPTY = { name: "", item: "", code: "", phone: "", email: "", date: "", status: "sospeso", notes: "", user_id: null };

export function OrderDialog({ order, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [focusName, setFocusName] = useState(false);
  useEffect(() => { if (order) setF({ ...EMPTY, date: todayIso(), ...order }); }, [order]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name: f.name, item: f.item, code: f.code, email: f.email, phone: f.phone, date: f.date || null, status: f.status, notes: f.notes, user_id: f.user_id };
      const { data } = f.id ? await api.put(`/admin/counter-orders/${f.id}`, body) : await api.post("/admin/counter-orders", body);
      toast.success(f.id ? "Ordine aggiornato" : "Ordine salvato");
      onSaved(data);
    } catch (x) { toast.error(errMsg(x)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="counter-order-dialog">
        <DialogHeader><DialogTitle className="text-2xl font-extrabold uppercase">{f.id ? "Modifica ordine" : "Nuovo ordine"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-3">
          <Field label="Nome cliente *" wide>
            <div className="relative">
              <input data-testid="counter-name-input" required autoFocus className={inp} value={f.name} autoComplete="off"
                onFocus={() => setFocusName(true)} onBlur={() => setFocusName(false)} onChange={(e) => setF({ ...f, name: e.target.value, user_id: null })} placeholder="Scrivi: ti propongo i clienti del sito" />
              {focusName && !f.user_id && <Suggestions q={f.name} onPick={(u) => setF((p) => ({ ...p, name: u.name, phone: p.phone || u.phone, email: p.email || u.email, user_id: u.id }))} />}
            </div>
            {f.user_id && <span className="text-[11px] text-green-700" data-testid="counter-linked-customer">Collegato al cliente registrato</span>}
          </Field>
          <Field label="Articolo ordinato *" wide><textarea data-testid="counter-item-input" required rows={2} className={inp + " h-auto py-2"} value={f.item} onChange={set("item")} placeholder="es. Toner HP 85A nero" /></Field>
          <Field label="Codice prodotto"><input data-testid="counter-code-input" className={inp + " font-mono"} value={f.code} onChange={set("code")} placeholder="es. CE285A" /></Field>
          <Field label="Email cliente (per avvisarlo)"><input data-testid="counter-email-input" type="email" className={inp} value={f.email} onChange={set("email")} placeholder="cliente@email.it" /></Field>
          <Field label="Telefono (per WhatsApp)"><input data-testid="counter-phone-input" type="tel" className={inp} value={f.phone} onChange={set("phone")} placeholder="333 123 4567" /></Field>
          <Field label="Data ordine"><input data-testid="counter-date-input" type="date" className={inp} value={f.date} onChange={set("date")} /></Field>
          <Field label="Stato" wide>
            <div className="flex flex-wrap gap-2">
              {STATUS_KEYS.map((k) => (
                <button type="button" key={k} data-testid={`counter-status-${k}`} onClick={() => setF({ ...f, status: k })}
                  className={`press h-9 px-3 rounded-full border text-sm font-semibold flex items-center gap-2 ${f.status === k ? "border-brand-navy bg-brand-navy text-white" : "bg-white"}`}>
                  <span className={`w-2 h-2 rounded-full ${STATUS[k].dot}`} /> {STATUS[k].label}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Note (facoltative)" wide><input data-testid="counter-notes-input" className={inp} value={f.notes} onChange={set("notes")} /></Field>
          <button data-testid="counter-save-btn" disabled={busy} className="sm:col-span-2 press h-11 rounded-md bg-brand-yellow text-brand-navy font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva ordine
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
